import { ChangeEvent, FC, useCallback, useRef } from 'react';
import { useRecoilState } from 'recoil';
import { TextArea } from '@storybook';

import { PipelineSettingsFormState } from 'global-stores';
import { DropOption } from "../../../components";
import { DYNAMIC_VARIABLES } from "./constants";
import cn from "classnames";

interface ITextAreaComponent {
  label: string;
  name: string;
  defaultValue?: string;
  disable?: boolean;
  placeholder?: string;
}

export const TextAreaComponent: FC<ITextAreaComponent> = ({
  label,
  name,
  disable = false,
  placeholder
}) => {
  const [settingsForm, setSettingsForm] = useRecoilState(
    PipelineSettingsFormState
  );

  const handleInputChange = useCallback(
    (e: ChangeEvent<HTMLTextAreaElement>) => {
      const { value } = e.target;
      setSettingsForm(prev => {
        const prevState = { ...prev };
        prevState[name] = value;
        return { ...prevState };
      });
    },
    [name, setSettingsForm]
  );

  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const handleKeyDown = useCallback((e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key !== 'Backspace' && e.key !== 'Delete') return;
    if (name !== "deliveryMethodMessage" || !settingsForm?.customizeEmail) return;

    const textarea = textareaRef.current;
    if (!textarea) return;

    const { selectionStart, selectionEnd, value: currentText } = textarea;

    // If there's a selection, let default behavior handle it
    if (selectionStart !== selectionEnd) return;

    // Find bracketed variables in the text using string search instead of regex
    const findVariables = (text: string): Array<{ start: number, end: number, content: string }> => {
      const results: Array<{ start: number, end: number, content: string }> = [];
      let startPos = 0;

      while (startPos < text.length) {
        const openBracket = text.indexOf('{', startPos);
        if (openBracket === -1) break;

        const closeBracket = text.indexOf('}', openBracket);
        if (closeBracket === -1) break;

        results.push({
          start: openBracket,
          end: closeBracket + 1,
          content: text.substring(openBracket, closeBracket + 1)
        });

        startPos = closeBracket + 1;
      }

      return results;
    };

    // Find all variables
    const variables = findVariables(currentText);

    // Check each variable
    for (const variable of variables) {
      const varStart = variable.start;
      const varEnd = variable.end;

      // Check if cursor is within or adjacent to a variable
      const isWithinOrAdjacent = e.key === 'Backspace'
        ? (selectionStart > varStart && selectionStart <= varEnd)
        : (selectionStart >= varStart && selectionStart < varEnd);

      if (isWithinOrAdjacent) {
        e.preventDefault();

        // Delete the entire variable
        const newText = currentText.substring(0, varStart) + currentText.substring(varEnd);
        setSettingsForm(prev => ({
          ...prev,
          [name]: newText
        }));

        // Set cursor position after deletion
        requestAnimationFrame(() => {
          if (textarea) {
            textarea.focus();
            textarea.setSelectionRange(varStart, varStart);
          }
        });

        break;
      }
    }
  }, [name, setSettingsForm, settingsForm?.customizeEmail]);

  const insertDynamicVariable = useCallback((variable: string) => {
    if (!textareaRef.current) return;
    const ta = textareaRef.current;
    const start = ta.selectionStart;
    const end = ta.selectionEnd;

    const newMsg = settingsForm[name].slice(0, start) + variable + settingsForm[name].slice(end);
    setSettingsForm(prev => ({
      ...prev,
      [name]: newMsg
    }));

    // // add our new instance
    // setInstances((insts) => [
    //   ...insts,
    //   { id: nanoid(), type: pd.id, value: pd.value },
    // ]);
    // setShowPlaceholders(false);

    // restore cursor
    setTimeout(() => {
      ta.focus();
      const pos = start + variable.length;
      ta.setSelectionRange(pos, pos);
    }, 0);
  }, [name, setSettingsForm, settingsForm]);

  return (<div
    className={cn({ "pipeline-dynamic-variables-dropdown-textarea": name === "deliveryMethodMessage" && settingsForm?.customizeEmail })}>
    <TextArea
      label={label}
      value={settingsForm[name] as string}
      handleChange={handleInputChange}
      placeholder={placeholder as string}
      disable={disable}
      onKeyDown={handleKeyDown}
      ref={textareaRef}
    />

    {name === "deliveryMethodMessage" && settingsForm?.customizeEmail &&
      <DropOption className="pipeline-dynamic-variables-dropdown"
                  menuName={<>Dynamic Variables <i className="ri-arrow-down-s-fill" /></>}
                  options={DYNAMIC_VARIABLES} onClickOption={(option) => insertDynamicVariable(option?.value || "")} />}
  </div>);
};
